import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { BehaviorSubject, Subscription } from "rxjs";
import { map, take, tap } from "rxjs/operators";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { currentBuildingUnsetAction } from "src/components/shared-components/state/actions/building.action";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { environment } from "src/environments/environment";
import {
  DocumentSecurity,
  DocumentSecurityStatus,
  DocumentSecurityType,
} from "src/model/class/general classes/document-security";
import { EventService } from "./event.service";

@Injectable({
  providedIn: "root",
})
export class DocumentSecurityService implements OnDestroy {
  private _idArea: number = null;
  private _eventSubscriptions: Array<Subscription> = [];

  public documentsSubject = new BehaviorSubject<Array<DocumentSecurity>>([]);

  constructor(
    private _http: HttpClient,
    private _store: Store,
    private _eventService: EventService,
  ) {
    this.subscribeEvents();
  }

  /**
   * Release the store/event subscriptions kept for the service lifetime.
   */
  ngOnDestroy(): void {
    this.unsubscribeEvents();
  }

  /**
   * Fetch all security documents for the current area, also pushing them onto
   * documentsSubject for reactive consumers. Resolves to the HTTP status code
   * (e.g. 404) instead of a list when no area is set or the call is non-200.
   */
  public getDocuments(): Promise<Array<DocumentSecurity> | number> {
    if (!this._idArea) return Promise.resolve(404);

    return this._http
      .get<Array<DocumentSecurity>>(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_DOCUMENT_SECURITY}?idArea=${this._idArea}`,
        {
          observe: "response",
        },
      )
      .pipe(
        map((responseData: HttpResponse<Object>) => {
          if (responseData.status !== 200) {
            return responseData.status;
          }

          const list = (responseData.body || []) as Array<DocumentSecurity>;
          this.documentsSubject.next(list);
          return list;
        }),
        take(1),
      )
      .toPromise();
  }

  /**
   * Create a new document record for the current area (metadata only; the file
   * is uploaded separately via the presigned-URL flow). Resolves the area id
   * defensively to cover the first create right after entering the page.
   */
  public async createDocument(
    payload: Partial<DocumentSecurity>,
  ): Promise<DocumentSecurity> {
    const idArea = await this.resolveIdArea();

    return this._http
      .post<DocumentSecurity>(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_DOCUMENT_SECURITY}`,
        {
          idArea,
          documentType: payload.documentType,
          expirationDate: payload.expirationDate,
          filename: payload.filename || "",
        },
      )
      .toPromise();
  }

  /**
   * Resolve the current area id. The store subscription keeps `_idArea` in sync,
   * but it can still be unset on the very first create after entering the page
   * (subscription not yet emitted). Fall back to a fresh, synchronous read of
   * the selected building from the store so the area — which always exists in
   * this context — is reliably attached to the request.
   */
  private async resolveIdArea(): Promise<number> {
    if (this._idArea && this._idArea > 0) {
      return this._idArea;
    }

    const building = await this._store
      .select(selectedBuilding)
      .pipe(take(1))
      .toPromise();

    return building?.idAreaLocal;
  }

  /**
   * Update the metadata (type, expiration date, filename) of an existing
   * document. The file itself is replaced separately via the upload flow.
   */
  public updateDocument(
    id: number,
    payload: Partial<DocumentSecurity>,
  ): Promise<DocumentSecurity> {
    return this._http
      .patch<DocumentSecurity>(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_DOCUMENT_SECURITY}/${id}`,
        {
          documentType: payload.documentType,
          expirationDate: payload.expirationDate,
          filename: payload.filename || "",
        },
      )
      .toPromise();
  }

  /**
   * Delete a document record (and its associated file) by id; resolves to
   * whether the request succeeded.
   */
  public deleteDocument(id: number): Promise<boolean> {
    return this._http
      .delete(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_DOCUMENT_SECURITY}/${id}`,
        {
          observe: "response",
        },
      )
      .pipe(
        map((responseData: HttpResponse<Object>) => responseData.ok),
        take(1),
      )
      .toPromise();
  }

  /**
   * Step 1 of the direct-to-S3 upload: ask the backend for a short-lived
   * presigned PUT URL. No file bytes travel through our API server.
   */
  public getUploadUrl(documentId: number, namefile: string): Promise<string> {
    return this._http
      .get<{ url: string }>(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_DOCUMENT_SECURITY}/uploadfile/${documentId}/url?namefile=${encodeURIComponent(
          namefile,
        )}`,
      )
      .pipe(
        map((response) => response?.url),
        take(1),
      )
      .toPromise();
  }

  /**
   * Step 2 of the direct-to-S3 upload: PUT the raw file straight to S3 over
   * HTTPS using the presigned URL.
   *
   * This intentionally uses the native fetch() API instead of Angular's
   * HttpClient: the AuthenticationInterceptor attaches an `Authorization:
   * Bearer ...` header (and `Content-Type: application/json`) to every
   * HttpClient request, which would break the SigV4-signed S3 request and leak
   * the session token to S3. fetch() bypasses the interceptor entirely.
   */
  public async uploadFileToPresignedUrl(
    url: string,
    file: File,
  ): Promise<void> {
    const response = await fetch(url, {
      method: "PUT",
      body: file,
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed with status ${response.status}`);
    }
  }

  /**
   * Step 3 of the direct-to-S3 upload: tell the backend the bytes are on S3 so
   * it binds the file to the record (same deterministic key the presigned URL
   * was generated for).
   */
  public confirmUpload(documentId: number, namefile: string): Promise<boolean> {
    return this._http
      .put(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_DOCUMENT_SECURITY}/uploadfile/${documentId}?namefile=${encodeURIComponent(
          namefile,
        )}`,
        {},
        {
          observe: "response",
        },
      )
      .pipe(
        map((responseData: HttpResponse<Object>) => responseData.ok),
        take(1),
      )
      .toPromise();
  }

  /**
   * Retrieve the stored file for a document, returned by the backend as a
   * serialized buffer ({ type: "Buffer", data: [...] }) for the caller to
   * rebuild into a blob.
   */
  public getUploadedFile(documentId: number): Promise<any> {
    return this._http
      .get(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_DOCUMENT_SECURITY}/file/${documentId}`,
      )
      .pipe(
        map((responseData: any) => responseData),
        take(1),
      )
      .toPromise();
  }

  /**
   * Lightweight area-level check (used outside the list page, e.g. for badges)
   * reporting whether the area has critical expired documents. Resolves to a
   * safe default when no area is set.
   */
  public getDocumentSecurityStatus(): Promise<DocumentSecurityStatus> {
    if (!this._idArea) {
      return Promise.resolve({ hasCriticalExpiredDocuments: false });
    }

    return this._http
      .get<DocumentSecurityStatus>(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_DOCUMENT_SECURITY}/status?idArea=${this._idArea}`,
      )
      .pipe(
        map((responseData: DocumentSecurityStatus) => responseData),
        take(1),
      )
      .toPromise();
  }

  /**
   * Load the global document type catalog (active types) used to populate the
   * selection dropdown. Available to any authenticated user.
   */
  public getDocumentTypes(): Promise<Array<DocumentSecurityType>> {
    return this._http
      .get<Array<DocumentSecurityType>>(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_DOCUMENT_SECURITY}/types`,
      )
      .pipe(
        map((response) => response || []),
        take(1),
      )
      .toPromise();
  }

  /**
   * Create a new document type. Super admin only (enforced server-side).
   */
  public createDocumentType(name: string): Promise<DocumentSecurityType> {
    return this._http
      .post<DocumentSecurityType>(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_DOCUMENT_SECURITY}/types`,
        { name },
      )
      .pipe(take(1))
      .toPromise();
  }

  /**
   * Logically delete a document type. Super admin only (enforced server-side).
   */
  public deleteDocumentType(id: number): Promise<boolean> {
    return this._http
      .delete(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_DOCUMENT_SECURITY}/types/${id}`,
        {
          observe: "response",
        },
      )
      .pipe(
        map((responseData: HttpResponse<Object>) => responseData.ok),
        take(1),
      )
      .toPromise();
  }

  /**
   * Keep `_idArea` in sync with the selected building from the store, and clear
   * the current building on logout so stale area state is not reused.
   */
  private subscribeEvents(): void {
    this._eventSubscriptions = [];

    this._eventSubscriptions.push(
      this._store
        .select(selectedBuilding)
        .pipe(
          map((building: Building) => building?.idAreaLocal),
          tap((idArea) => {
            this._idArea = idArea;
          }),
        )
        .subscribe(),
    );

    this._eventSubscriptions.push(
      this._eventService?.OnMakeLogout.subscribe(() => {
        this._store.dispatch(currentBuildingUnsetAction());
      }),
    );
  }

  /**
   * Unsubscribe from all tracked store/event subscriptions.
   */
  private unsubscribeEvents(): void {
    this._eventSubscriptions.forEach((subscription) =>
      subscription?.unsubscribe(),
    );
  }
}
